import React from 'react';
import { SEVERITY, OSHA_CATEGORY, WEEKDAYS, formatDateKey, getMonthDays, worstSeverityOf } from '../constants';

// Month grid. Auto-green: past/today days with no entries render as No Issues;
// future empty days render neutral. Day tint reflects the worst severity present.
export default function CalendarGrid({ currentMonth, entriesByDay, todayKey, onSelectDay }) {
  const days = getMonthDays(currentMonth);

  return (
    <div className="rounded-lg border border-slate-700 overflow-hidden">
      <div className="grid grid-cols-7 gap-px bg-slate-700">
        {WEEKDAYS.map((d) => (
          <div key={d} className="bg-slate-800 text-[10px] tracking-widest text-slate-400 px-3 py-2 font-medium">{d}</div>
        ))}
        {days.map((day, idx) => {
          const key = formatDateKey(day.date);
          const dayEntries = entriesByDay[key] || [];
          const hasEntries = dayEntries.length > 0;
          const worst = worstSeverityOf(dayEntries);
          const sev = worst ? SEVERITY[worst] : null;
          const isToday = key === todayKey;
          const isPastOrToday = key <= todayKey;
          const showAutoGreen = !hasEntries && day.current && isPastOrToday;

          const oshaEntries = dayEntries.filter((e) => e.category === OSHA_CATEGORY);
          const hasOSHA = oshaEntries.length > 0;
          const allNotified = hasOSHA && oshaEntries.every((e) => e.oshaNotified);

          const sorted = hasEntries
            ? [...dayEntries].sort((a, b) => (SEVERITY[b.severity]?.rank || 0) - (SEVERITY[a.severity]?.rank || 0))
            : [];

          let bg = 'bg-slate-800 hover:bg-slate-700/70';
          if (sev) bg = `${sev.tint} hover:brightness-125`;
          else if (showAutoGreen) bg = 'bg-emerald-500/10 hover:bg-emerald-500/20';

          return (
            <button
              key={idx}
              onClick={() => onSelectDay(key)}
              className={`relative min-h-[96px] md:min-h-[116px] p-2 text-left transition-all ${bg} ${day.current ? '' : 'opacity-40'} ${isToday ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}`}
            >
              {sev && <div className={`absolute top-0 left-0 right-0 h-1 ${sev.bar}`} />}
              <div className="flex items-start justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold text-white tabular-nums leading-none">{day.date.getDate()}</span>
                  {dayEntries.length > 1 && <span className="text-[10px] font-bold text-slate-300">×{dayEntries.length}</span>}
                </div>
                {isToday && <span className="text-[9px] tracking-widest text-blue-400 font-bold mt-1">TODAY</span>}
              </div>

              {hasEntries && (
                <div className="mt-1.5 space-y-0.5">
                  {hasOSHA && (
                    <div className={`text-[8px] font-bold tracking-wider inline-block px-1.5 py-0.5 rounded ${allNotified ? 'bg-slate-600 text-white' : 'bg-red-600 text-white'}`}>
                      OSHA {allNotified ? '✓' : '!'}
                    </div>
                  )}
                  {sorted.slice(0, 2).map((e) => {
                    const eSev = SEVERITY[e.severity];
                    return (
                      <div key={e.id} className="flex items-center gap-1.5 leading-tight">
                        <span className={`w-1.5 h-1.5 rounded-sm flex-shrink-0 ${eSev?.dot}`} />
                        <span className="text-[11px] font-medium text-slate-200 truncate">{e.category || eSev?.label}</span>
                      </div>
                    );
                  })}
                  {dayEntries.length > 2 && (
                    <div className="text-[9px] tracking-widest text-slate-400 font-bold">+{dayEntries.length - 2} MORE</div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
